import { create } from "zustand";
import { DEMO_PEOPLE, setPeopleRef, getPeopleRef, buildTreeLayout, getFullName } from "../data/family";
import {
  fetchNotifications,
  resolveNotification as apiResolveNotification,
  resolveEquivalence,
  acceptAccessInvitation,
  acceptAccessRequest,
  acceptIdentityClaim,
  rejectIdentityClaim,
  subscribeToNotifications,
  fetchTreeMembers,
  revokeAccess,
  grantAccess,
  clearResolvedNotifications,
} from "../lib/api/collaboration";
import { fetchAuditLog, recordAuditEntry } from "../lib/api/auditLog";
import { updateTree as apiUpdateTree } from "../lib/api/trees";
import {
  signIn,
  signUp,
  signOut,
  onAuthChange,
  getProfile,
  updateProfile as apiUpdateProfile,
  updateUserMetadata,
  sendPasswordReset,
  updatePassword as apiUpdatePassword,
  deleteAccount as apiDeleteAccount,
} from "../lib/api/auth";
import { fetchPeople, insertPerson, updatePerson as apiUpdatePerson, deletePerson as apiDeletePerson, deletePersonWithSubtree } from "../lib/api/people";
import { supabase } from "../lib/supabase";
import { detectBrowserLanguage } from "../lib/browserLanguage";

let _notifChannel = null;
let _authUnsubscribe = null;

/**
 * Supabase Realtime reuses channels by topic. If two `startRealtime` runs overlap
 * (e.g. rapid auth events on sign-up), the second sees an already-subscribed channel
 * and `.on('postgres_changes', …)` throws. Serialize teardown + subscribe; use
 * `removeChannel` so the topic slot is freed before subscribing again.
 */
let _realtimeQueue = Promise.resolve();

function enqueueRealtime(fn) {
  _realtimeQueue = _realtimeQueue.then(fn).catch((err) => {
    console.error(err);
  });
  return _realtimeQueue;
}

function applyTheme(theme) {
  document.documentElement.dataset.theme = theme === "dark" ? "dark" : "light";
}

export const useStore = create((set, get) => ({
  // ── People (source of truth for the active tree) ──────────────
  people: DEMO_PEOPLE,

  // ── Tree layout ───────────────────────────────────────────────
  rootPersonId: "ibrahima",
  nodes: [],
  edges: [],
  families: [],
  positions: {},

  // ── UI state ──────────────────────────────────────────────────
  selectedPersonId: "ibrahima",
  panelOpen: false,
  searchQuery: "",
  searchResults: [],
  treeLoading: false,

  // ── Modal ─────────────────────────────────────────────────────
  activeModal: null,
  openModal: (name) => set({ activeModal: name }),
  closeModal: () => set({ activeModal: null }),

  // ── Toasts ───────────────────────────────────────────────────
  toasts: [],
  addToast: ({ type = "info", message }) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    set((s) => ({ toasts: [...s.toasts, { id, type, message }] }));
    setTimeout(() => get().removeToast(id), 4200);
    return id;
  },
  removeToast: (id) => set((s) => ({ toasts: s.toasts.filter((toast) => toast.id !== id) })),

  // ── Mobile sidebar ────────────────────────────────────────────
  sidebarOpen: false,
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  closeSidebar: () => set({ sidebarOpen: false }),

  // ── Auth ──────────────────────────────────────────────────────
  authStatus: "guest",       // "guest" | "logged_in"
  authInitialized: false,    // prevents login screen flash on reload
  currentUser: null,         // { id, name, email, initials }

  initAuth: () => {
    if (_authUnsubscribe) _authUnsubscribe();
    _authUnsubscribe = onAuthChange(async (user) => {
      if (user) {
        const { profile } = await getProfile(user.id);
        const name = profile?.full_name ?? user.email.split("@")[0];
        const initials = name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
        const language = profile?.language ?? detectBrowserLanguage();
        applyTheme(profile?.theme ?? "light");
        set({
          authStatus: "logged_in",
          currentUser: {
            id: user.id,
            name,
            email: user.email,
            initials,
            avatarUrl: profile?.avatar_url ?? null,
            language,
            theme: profile?.theme ?? "light",
            notificationsEnabled: profile?.notifications_enabled ?? true,
            claimedPersonId: null,
          },
          authInitialized: true,
        });
        get().loadMyTrees();
        get().loadNotifications();
        get().startRealtime(user.id);
      } else {
        get().stopRealtime();
        setPeopleRef([]);
        applyTheme("light");
        set({
          authStatus: "guest",
          currentUser: null,
          people: [],
          myTrees: [],
          linkedTrees: [],
          treeMembers: [],
          bridgeNodes: {},
          notifications: [],
          currentTreeId: null,
          authInitialized: true,
          nodes: [],
          edges: [],
          families: [],
          positions: {},
          rootPersonId: null,
          selectedPersonId: null,
          panelOpen: false,
          readOnlyMode: false,
          viewingTree: null,
        });
      }
    });
  },

  login: async (email, password) => {
    const { user, error } = await signIn(email, password);
    return !error && !!user;
  },

  register: async (email, password, fullName) => {
    const { user, error } = await signUp(email, password, fullName);
    return !error && !!user;
  },

  updateAccountProfile: async ({ fullName }) => {
    const trimmed = fullName.trim();
    if (!trimmed) return { error: new Error("Name is required") };

    const initials = trimmed
      .split(/\s+/)
      .map((word) => word[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();

    const [{ profile, error: profileError }, { error: metadataError }] = await Promise.all([
      apiUpdateProfile({ full_name: trimmed, initials }),
      updateUserMetadata({ full_name: trimmed }),
    ]);

    const error = profileError || metadataError;
    if (error) return { error };

    set((s) => ({
      currentUser: s.currentUser
        ? { ...s.currentUser, name: profile?.full_name ?? trimmed, initials: profile?.initials ?? initials }
        : s.currentUser,
    }));
    return { error: null };
  },

  updateAccountPreferences: async (updates) => {
    const dbUpdates = {};
    if ("language" in updates) dbUpdates.language = updates.language;
    if ("theme" in updates) dbUpdates.theme = updates.theme;
    if ("notificationsEnabled" in updates) dbUpdates.notifications_enabled = updates.notificationsEnabled;
    if ("avatarUrl" in updates) dbUpdates.avatar_url = updates.avatarUrl;

    const { profile, error } = await apiUpdateProfile(dbUpdates);
    if (error) return { error };

    const theme = profile?.theme ?? updates.theme;
    if (theme) applyTheme(theme);

    set((s) => ({
      currentUser: s.currentUser
        ? {
            ...s.currentUser,
            avatarUrl: profile?.avatar_url ?? s.currentUser.avatarUrl,
            language: profile?.language ?? s.currentUser.language,
            theme: profile?.theme ?? s.currentUser.theme,
            notificationsEnabled: profile?.notifications_enabled ?? s.currentUser.notificationsEnabled,
          }
        : s.currentUser,
    }));
    if (updates.notificationsEnabled === true) get().loadNotifications();
    if (updates.notificationsEnabled === false) set({ notifications: [] });
    return { error: null };
  },

  sendAccountPasswordReset: async () => {
    const { currentUser } = get();
    if (!currentUser?.email) return { error: new Error("No email on account") };
    return sendPasswordReset(currentUser.email);
  },

  updateAccountPassword: async (password) => {
    if (password.length < 6) return { error: new Error("Password too short") };
    const { error } = await apiUpdatePassword(password);
    return { error };
  },

  deleteAccount: async () => {
    const { error } = await apiDeleteAccount();
    return { error };
  },

  logout: async () => {
    await signOut();
    // onAuthChange handles state cleanup
  },

  // ── Tree data ──────────────────────────────────────────────────
  currentTreeId: null,
  myTrees: [],      // [{ id, name }]
  linkedTrees: [],  // [{ id, name, ownerName, role, status }]
  bridgeNodes: {},  // { personId: { status, canAccess, otherTreeId, otherTreeName } }
  treeMembers: [],  // [{ id, name, initials, email, role }]

  loadMyTrees: async () => {
    const { currentUser } = get();
    if (!currentUser) return;

    const [ownedRes, memberRes] = await Promise.all([
      supabase
        .from("trees")
        .select("id, name, is_private")
        .eq("owner_id", currentUser.id)
        .order("created_at"),
      supabase
        .from("tree_members")
        .select("tree_id, role, trees(id, name, profiles!trees_owner_id_fkey(full_name))")
        .eq("user_id", currentUser.id),
    ]);

    const myTrees = (ownedRes.data ?? []).map((t) => ({ id: t.id, name: t.name, isPrivate: t.is_private }));
    const linkedTrees = (memberRes.data ?? []).map((m) => ({
      id: m.tree_id,
      name: m.trees?.name ?? "Arbre lié",
      ownerName: m.trees?.profiles?.full_name ?? "Inconnu",
      role: m.role,
      status: "linked",
    }));

    set({ myTrees, linkedTrees });

    if (myTrees.length > 0) {
      await get().loadTree(myTrees[0].id);
    } else if (linkedTrees.length > 0) {
      await get().enterReadOnly(linkedTrees[0]);
    } else {
      // New user with no trees — clear any demo/stale state; UI will prompt them to create one
      setPeopleRef([]);
      set({ people: [], nodes: [], edges: [], families: [], positions: {}, rootPersonId: null, selectedPersonId: null, currentTreeId: null });
    }
  },

  loadTree: async (treeId) => {
    set({ treeLoading: true });
    const { people: fetched, error } = await fetchPeople(treeId);
    if (error) { set({ treeLoading: false }); return; }

    if (!fetched || fetched.length === 0) {
      setPeopleRef([]);
      set({
        people: [],
        currentTreeId: treeId,
        nodes: [], edges: [], families: [], positions: {},
        rootPersonId: null, selectedPersonId: null,
        treeLoading: false,
      });
      return;
    }

    // Leaving a linked tree — clear read-only flag before setting new tree state
    set({ readOnlyMode: false, viewingTree: null });

    setPeopleRef(fetched);
    const claimedPerson = get().currentUser
      ? fetched.find((p) => p.claimedByUserId === get().currentUser.id)
      : null;

    const { data: treeMeta, error: treeMetaErr } = await supabase
      .from("trees")
      .select("owner_id, root_person_id")
      .eq("id", treeId)
      .maybeSingle();
    const treePrefs = treeMetaErr ? null : treeMeta;

    // Pick the oldest person with no parents as root (patriarch/matriarch).
    const parentless = fetched.filter((p) => !p.parentIds || p.parentIds.length === 0);
    const defaultRoot = parentless.reduce(
      (oldest, p) => ((p.birthYear ?? Infinity) < (oldest.birthYear ?? Infinity) ? p : oldest),
      parentless[0] ?? fetched[0]
    );

    let preferredRootId = treePrefs?.root_person_id ?? null;
    const uid = get().currentUser?.id;
    if (!preferredRootId && uid && treePrefs?.owner_id !== uid) {
      try {
        preferredRootId = localStorage.getItem(`denbaya-root-${treeId}`) || null;
      } catch {
        preferredRootId = null;
      }
    }

    const root =
      preferredRootId && fetched.some((p) => p.id === preferredRootId)
        ? fetched.find((p) => p.id === preferredRootId)
        : defaultRoot;

    const layout = buildTreeLayout(root.id);

    // Restore last-selected person for this tree so returning doesn't
    // always jump back to the root.
    let restoredSelection = null;
    try {
      restoredSelection = localStorage.getItem(`denbaya-sel-${treeId}`) || null;
    } catch { /* ignore */ }
    const validSelection =
      restoredSelection && fetched.some((p) => p.id === restoredSelection)
        ? restoredSelection
        : null;

    set({
      people: fetched,
      currentTreeId: treeId,
      rootPersonId: root.id,
      selectedPersonId: validSelection ?? root.id,
      panelOpen: false,          // don't auto-open panel on load
      currentUser: get().currentUser
        ? { ...get().currentUser, claimedPersonId: claimedPerson?.id ?? null }
        : get().currentUser,
      ...layout,
      treeLoading: false,
    });

    get().loadBridgeNodes(treeId);
    get().loadTreeMembers(treeId);
  },

  loadBridgeNodes: async (treeId) => {
    const { data, error } = await supabase.rpc("get_bridge_nodes", { p_tree_id: treeId });
    if (error || !data) return;
    const bridgeNodes = {};
    for (const row of data) {
      bridgeNodes[row.person_id] = {
        status: row.status,
        canAccess: row.can_access,
        otherTreeId: row.other_tree_id,
        otherTreeName: row.other_tree_name,
        otherPersonId: row.other_person_id ?? null,
      };
    }
    set({ bridgeNodes });
  },

  loadTreeMembers: async (treeId) => {
    const { members, error } = await fetchTreeMembers(treeId);
    if (error) return;
    const mapped = (members ?? []).map((m) => ({
      id: m.user_id,
      name: m.profiles?.full_name ?? "Inconnu",
      initials: m.profiles?.initials ?? "??",
      email: "",
      role: m.role,
    }));
    set({ treeMembers: mapped });
  },

  revokeMember: async (userId) => {
    const { currentTreeId } = get();
    if (!currentTreeId) return;
    await revokeAccess(currentTreeId, userId);
    get().loadTreeMembers(currentTreeId);
  },

  changeMemberRole: async (userId, newRole) => {
    const { currentTreeId } = get();
    if (!currentTreeId) return;
    await grantAccess(currentTreeId, userId, newRole);  // upserts on (tree_id, user_id)
    get().loadTreeMembers(currentTreeId);
  },

  /** Owner: rename a tree they own. Updates DB + local myTrees list. */
  renameTree: async (treeId, newName) => {
    const trimmed = (newName ?? "").trim();
    if (!trimmed) return false;
    const { tree, error } = await apiUpdateTree(treeId, { name: trimmed });
    if (error || !tree) return false;
    set((s) => ({
      myTrees: s.myTrees.map((t) => t.id === treeId ? { ...t, name: tree.name } : t),
    }));
    return true;
  },

  updateTreePrivacy: async (treeId, isPrivate) => {
    const { tree, error } = await apiUpdateTree(treeId, { is_private: isPrivate });
    if (error || !tree) return false;
    set((s) => ({
      myTrees: s.myTrees.map((t) => t.id === treeId ? { ...t, isPrivate: tree.is_private } : t),
    }));
    return true;
  },

  /** Member: leave a linked tree (delete own tree_members row). */
  leaveTree: async (treeId) => {
    const { currentUser, currentTreeId, linkedTrees, myTrees } = get();
    if (!currentUser) return false;
    const { error } = await supabase
      .from("tree_members")
      .delete()
      .eq("tree_id", treeId)
      .eq("user_id", currentUser.id);
    if (error) return false;

    set({ linkedTrees: linkedTrees.filter((t) => t.id !== treeId) });

    // If we were viewing the tree we just left, fall back to one of our own.
    if (currentTreeId === treeId) {
      if (myTrees.length > 0) {
        await get().loadTree(myTrees[0].id);
      } else {
        setPeopleRef([]);
        set({
          people: [], nodes: [], edges: [], families: [], positions: {},
          rootPersonId: null, selectedPersonId: null, currentTreeId: null,
          readOnlyMode: false, viewingTree: null,
        });
      }
    }
    return true;
  },

  createTree: async (name) => {
    const { currentUser } = get();
    if (!currentUser) return;
    const { data: newTree } = await supabase
      .from("trees")
      .insert({ owner_id: currentUser.id, name: name.trim() })
      .select("id, name, is_private")
      .single();
    if (!newTree) return;
    set((s) => ({ myTrees: [...s.myTrees, { id: newTree.id, name: newTree.name, isPrivate: newTree.is_private }] }));
    await get().loadTree(newTree.id);
  },

  deleteTree: async (treeId) => {
    const { myTrees, currentTreeId } = get();
    const { error } = await supabase.from("trees").delete().eq("id", treeId);
    if (error) return false;

    const updated = myTrees.filter((t) => t.id !== treeId);
    set({ myTrees: updated });

    if (currentTreeId === treeId) {
      if (updated.length > 0) {
        await get().loadTree(updated[0].id);
      } else {
        // No trees left — clear canvas; UI will prompt them to create a new one
        setPeopleRef([]);
        set({ people: [], nodes: [], edges: [], families: [], positions: {}, rootPersonId: null, selectedPersonId: null, currentTreeId: null });
      }
    }
    return true;
  },

  // ── Read-only mode ────────────────────────────────────────────
  readOnlyMode: false,
  viewingTree: null,
  enterReadOnly: async (tree, personId) => {
    await get().loadTree(tree.id);
    const canEditTree = tree.role === "edit" || tree.role === "admin";
    set({ readOnlyMode: !canEditTree, viewingTree: tree });
    if (personId) {
      // loadTree already selected the root; let React render + FocusController
      // consume its firstRun skip, then pan to the linked person in the next frame.
      setTimeout(() => set({ selectedPersonId: personId, panelOpen: true }), 200);
    }
  },
  exitReadOnly: async () => {
    set({ readOnlyMode: false, viewingTree: null });
    const { myTrees } = get();
    if (myTrees.length > 0) await get().loadTree(myTrees[0].id);
  },

  // ── Comments ──────────────────────────────────────────────────
  comments: {},
  addComment: (personId, text) => {
    const { currentUser } = get();
    const comment = {
      id: `c${Date.now()}`,
      authorName: currentUser?.name ?? "Anonyme",
      text: text.trim(),
      createdAt: new Date().toISOString().split("T")[0],
    };
    set((s) => ({
      comments: { ...s.comments, [personId]: [...(s.comments[personId] ?? []), comment] },
    }));
  },

  // ── Audit log ─────────────────────────────────────────────────
  auditLog: [],   // populated on demand by loadAuditLog (DB) or for demo mode (in-memory)

  loadAuditLog: async () => {
    const { currentTreeId } = get();
    if (!currentTreeId) { set({ auditLog: [] }); return; }
    const { log } = await fetchAuditLog(currentTreeId);
    const entries = log.map((row) => ({
      id:         row.id,
      action:     row.action,
      personName: row.person_name,
      personId:   row.person_id,
      fields:     row.fields,
      by:         row.user?.full_name ?? "Inconnu",
      at:         row.created_at?.slice(0, 10) ?? "",
    }));
    set({ auditLog: entries });
  },

  // ── Notifications ─────────────────────────────────────────────
  notifications: [],

  loadNotifications: async () => {
    if (get().currentUser?.notificationsEnabled === false) {
      set({ notifications: [] });
      return;
    }
    const { notifications, error } = await fetchNotifications();
    if (error) {
      get().addToast({ type: "error", message: "Impossible de charger les notifications." });
      return;
    }
    set({ notifications: notifications ?? [] });
  },

  dismissNotification: async (id) => {
    const { notification, error } = await apiResolveNotification(id, "dismissed");
    if (error || !notification) {
      get().addToast({ type: "error", message: "Impossible d'ignorer cette notification." });
      return;
    }
    set((s) => ({ notifications: s.notifications.filter((n) => n.id !== id) }));
  },

  clearResolvedNotifications: async () => {
    await clearResolvedNotifications();
    set((s) => ({ notifications: s.notifications.filter((n) => n.status === "pending") }));
  },

  resolveNotification: async (id, resolution) => {
    const { notifications, currentTreeId } = get();
    const notif = notifications.find((n) => n.id === id);

    // Both grant flows must run BEFORE apiResolveNotification because the RPCs
    // require status = 'pending' in the DB to validate the action.
    //   access_invitation: owner invited ME       → I accept → I get into tree_members
    //   access_request:    user requested MY tree → I accept → THEY get into tree_members
    if (notif?.type === "access_invitation" && resolution === "approved") {
      const { ok, error } = await acceptAccessInvitation(id);
      if (error || !ok) return false;
    }
    if (notif?.type === "access_request" && resolution === "approved") {
      const { ok, error } = await acceptAccessRequest(id);
      if (error || !ok) return false;
    }
    if (notif?.type === "claim_request") {
      const result = resolution === "approved"
        ? await acceptIdentityClaim(id)
        : await rejectIdentityClaim(id);
      if (result.error || !result.ok) return false;
      if (currentTreeId) await get().loadTree(currentTreeId);
    }

    // For equivalence proposals, also update the underlying person_equivalences row
    if (notif?.type === "equivalence_proposal" && notif.payload?.equivalence_id) {
      const { ok, error } = await resolveEquivalence(notif.payload.equivalence_id, resolution);
      if (error || !ok) return false;
    }

    const { error: resolveError } = await apiResolveNotification(id, resolution);
    if (resolveError) return false;

    // After accepting an invitation, refresh my trees so the new linked tree appears
    if (notif?.type === "access_invitation" && resolution === "approved") {
      await get().loadMyTrees();
    }

    set((s) => ({
      notifications: s.notifications.map((n) =>
        n.id === id ? { ...n, status: resolution } : n
      ),
    }));

    // Refresh bridge badges so nodes reflect the new status immediately
    if (currentTreeId) get().loadBridgeNodes(currentTreeId);
    return true;
  },

  startRealtime: (userId) => {
    enqueueRealtime(async () => {
      if (_notifChannel) {
        await supabase.removeChannel(_notifChannel);
        _notifChannel = null;
      }
      _notifChannel = subscribeToNotifications(userId, (newNotif) => {
        if (get().currentUser?.notificationsEnabled === false) return;
        get().loadNotifications();
        if (newNotif.type === "access_granted") {
          get().loadMyTrees();
        }
      });
    });
  },

  stopRealtime: () => {
    enqueueRealtime(async () => {
      if (_notifChannel) {
        await supabase.removeChannel(_notifChannel);
        _notifChannel = null;
      }
    });
  },

  // ── Tree layout actions ───────────────────────────────────────
  initTree: () => {
    const { rootPersonId } = get();
    if (!rootPersonId) return;
    set(buildTreeLayout(rootPersonId));
  },

  setRoot: async (id) => {
    set({ rootPersonId: id, selectedPersonId: id, ...buildTreeLayout(id) });
    const { currentTreeId, currentUser } = get();
    if (!currentTreeId || !currentUser?.id) return;

    const { data: treeMeta } = await supabase
      .from("trees")
      .select("owner_id")
      .eq("id", currentTreeId)
      .maybeSingle();

    if (treeMeta?.owner_id === currentUser.id) {
      await apiUpdateTree(currentTreeId, { root_person_id: id });
    } else {
      try {
        localStorage.setItem(`denbaya-root-${currentTreeId}`, id);
      } catch {
        /* ignore */
      }
    }
  },

  selectPerson: (id, { expandPanel = true } = {}) => {
    set({ selectedPersonId: id, panelOpen: expandPanel });
    // Remember selection so returning to this tree resumes here
    const treeId = get().currentTreeId;
    if (treeId && id) {
      try { localStorage.setItem(`denbaya-sel-${treeId}`, id); } catch { /* ignore */ }
    }
  },
  expandPanel: () => set({ panelOpen: true }),
  closePanel: () => set({ panelOpen: false }),

  // ── Search ────────────────────────────────────────────────────
  setSearch: (query) => {
    const q = query.toLowerCase().trim();
    if (!q) { set({ searchQuery: query, searchResults: [] }); return; }
    const results = getPeopleRef().filter(
      (p) =>
        p.firstName.toLowerCase().includes(q) ||
        p.lastName.toLowerCase().includes(q) ||
        (p.middleName && p.middleName.toLowerCase().includes(q)) ||
        (p.nickname && p.nickname.toLowerCase().includes(q)) ||
        p.city?.toLowerCase().includes(q) ||
        p.email?.toLowerCase().includes(q)
    );
    set({ searchQuery: query, searchResults: results });
  },
  clearSearch: () => set({ searchQuery: "", searchResults: [] }),

  // ── CRUD ──────────────────────────────────────────────────────
  /** Returns the new person's ID (or null on failure). */
  addPerson: async (personData) => {
    const { currentTreeId, currentUser } = get();
    let personId;

    if (currentTreeId) {
      const { person, error } = await insertPerson(currentTreeId, personData);
      if (error || !person) return null;
      personId = person.id;

      // When adding a parent to the current root, auto-promote the new parent
      // as root so the tree naturally expands upward.
      if (
        personData.connectionType === "parent_of" &&
        personData.connectedPersonId === get().rootPersonId
      ) {
        await apiUpdateTree(currentTreeId, { root_person_id: person.id });
      }

      await get().loadTree(currentTreeId);
    } else {
      // Demo mode: mutate in-memory array
      const { connectedPersonId, connectionType, parentRelation, spouseUnionStatus, coParentId, coParentRelation, ...fields } = personData;
      personId = `p${Date.now()}${Math.random().toString(36).slice(2, 5)}`;
      const person = {
        firstName: "", lastName: "", birthYear: null, birthDate: null, deathYear: null,
        middleName: null,
        nickname: null,
        gender: "male", phone: null, email: null, address: null,
        city: null, notes: null, spouseId: null, parentIds: [],
        ...fields,
        id: personId,
      };
      if (connectedPersonId && connectionType) {
        if (connectionType === "child_of") {
          person.parentIds = [connectedPersonId];
          person.parentLinkTypes = {
            [connectedPersonId]: parentRelation === "adoptive" ? "adoptive" : "biological",
          };
          if (
            coParentId &&
            coParentId !== connectedPersonId
          ) {
            person.parentIds.push(coParentId);
            person.parentLinkTypes[coParentId] =
              coParentRelation === "adoptive" ? "adoptive" : "biological";
          }
        } else if (connectionType === "sibling_of") {
          // Sibling: share the same parents as the connected person (if any)
          const sibling = getPeopleRef().find((p) => p.id === connectedPersonId);
          if (sibling && sibling.parentIds && sibling.parentIds.length > 0) {
            const rel = parentRelation === "adoptive" ? "adoptive" : "biological";
            person.parentIds = [...sibling.parentIds];
            person.parentLinkTypes = {};
            for (const pid of sibling.parentIds) {
              person.parentLinkTypes[pid] = rel;
            }
          }
          // Always store explicit sibling link (works even without parents)
          person.siblingIds = [connectedPersonId];
          if (sibling) {
            sibling.siblingIds = [...(sibling.siblingIds ?? []), personId];
          }
        } else if (connectionType === "parent_of") {
          const target = getPeopleRef().find((p) => p.id === connectedPersonId);
          if (target) {
            target.parentIds = [...(target.parentIds ?? []), personId];
            // Track biological vs adoptive on the child's parentLinkTypes
            const rel = parentRelation === "adoptive" ? "adoptive" : "biological";
            target.parentLinkTypes = { ...(target.parentLinkTypes ?? {}), [personId]: rel };
          }
        } else if (connectionType === "spouse_of") {
          const sp = getPeopleRef().find((p) => p.id === connectedPersonId);
          const isFormer = spouseUnionStatus === "former";
          const spHasPartner = !!(sp?.spouseId || (sp?.unions?.length ?? 0) > 0);
          if (!isFormer && !spHasPartner) {
            person.spouseId = connectedPersonId;
            if (sp) sp.spouseId = personId;
          } else {
            person.unions = [
              {
                spouseId: connectedPersonId,
                order: 0,
                unionStatus: isFormer ? "former" : "married",
              },
            ];
            delete person.spouseId;
            if (sp) {
              let u = sp.unions ? [...sp.unions] : [];
              if (sp.spouseId && !sp.unions?.length) {
                const prevSid = sp.spouseId;
                u = [{ spouseId: prevSid, order: 0, unionStatus: "married" }];
                delete sp.spouseId;
                const other = getPeopleRef().find((p) => p.id === prevSid);
                if (other?.spouseId === sp.id) {
                  other.unions = [{ spouseId: sp.id, order: 0, unionStatus: "married" }];
                  delete other.spouseId;
                }
              }
              u.push({
                spouseId: personId,
                order: u.length,
                unionStatus: isFormer ? "former" : "married",
              });
              sp.unions = u;
            }
          }
        }
      }
      const updatedPeople = [...getPeopleRef(), person];
      setPeopleRef(updatedPeople);

      // In demo mode, also auto-promote parent of root so tree grows upward.
      const newRoot =
        connectionType === "parent_of" && connectedPersonId === get().rootPersonId
          ? personId
          : get().rootPersonId;
      set({ people: updatedPeople, rootPersonId: newRoot, ...buildTreeLayout(newRoot) });
    }

    const personName = getFullName({
      firstName: personData.firstName,
      middleName: personData.middleName,
      lastName: personData.lastName,
    });
    if (currentTreeId) {
      await recordAuditEntry({ treeId: currentTreeId, action: "add", personId, personName });
    } else {
      const entry = { id: `log${Date.now()}`, action: "add", personName, personId,
        by: currentUser?.name ?? "Vous", at: new Date().toISOString().split("T")[0] };
      set((s) => ({ auditLog: [entry, ...s.auditLog] }));
    }
    return personId;
  },

  updatePerson: async (id, updates) => {
    const { currentTreeId, currentUser } = get();

    if (currentTreeId) {
      const { error } = await apiUpdatePerson(id, updates);
      if (error) return { error };
      await get().loadTree(currentTreeId);
    } else {
      const arr = getPeopleRef();
      const p = arr.find((x) => x.id === id);
      if (!p) return { error: new Error("Person not found") };
      const prevSpouseId = p.spouseId;
      // Keys explicitly set to undefined mean "remove this property" (e.g. clearing unions)
      for (const [k, v] of Object.entries(updates)) {
        if (v === undefined) delete p[k];
        else p[k] = v;
      }
      if ("spouseId" in updates && updates.spouseId !== prevSpouseId) {
        if (prevSpouseId) {
          const old = arr.find((x) => x.id === prevSpouseId);
          if (old?.spouseId === id) old.spouseId = null;
        }
        if (updates.spouseId) {
          const nw = arr.find((x) => x.id === updates.spouseId);
          if (nw && !nw.spouseId && !nw.unions?.length) nw.spouseId = id;
        }
      }
      set({ people: [...arr], ...buildTreeLayout(get().rootPersonId) });
    }

    const p = getPeopleRef().find((x) => x.id === id);
    const personName = p ? getFullName(p) : id;
    const fields = Object.keys(updates);
    if (currentTreeId) {
      const { error } = await recordAuditEntry({ treeId: currentTreeId, action: "edit", personId: id, personName, fields });
      if (error) return { error };
    } else {
      const entry = { id: `log${Date.now()}`, action: "edit", personName, personId: id, fields,
        by: currentUser?.name ?? "Vous", at: new Date().toISOString().split("T")[0] };
      set((s) => ({ auditLog: [entry, ...s.auditLog] }));
    }
    if (updates.claimedByUserId && updates.claimedByUserId === currentUser?.id) {
      set((s) => ({
        currentUser: s.currentUser ? { ...s.currentUser, claimedPersonId: id } : s.currentUser,
      }));
    }
    return { error: null };
  },

  /**
   * Remove a person AND all their descendants (subtree).
   */
  removePerson: async (id) => {
    const { currentTreeId, currentUser, rootPersonId, selectedPersonId, panelOpen } = get();
    const removed = getPeopleRef().find((p) => p.id === id);
    const personName = removed ? getFullName(removed) : id;

    if (currentTreeId) {
      const { error } = await deletePersonWithSubtree(id);
      if (error) return;
      await get().loadTree(currentTreeId);
    } else {
      const arr = getPeopleRef();
      // Collect all descendant IDs in the in-memory array
      function collectDescendants(pid) {
        const children = arr.filter((p) => p.parentIds?.includes(pid));
        return children.flatMap((c) => [c.id, ...collectDescendants(c.id)]);
      }
      const toRemove = new Set([id, ...collectDescendants(id)]);

      // Build a new array without removed people, cleaning up references
      const remaining = arr
        .filter((p) => !toRemove.has(p.id))
        .map((p) => ({
          ...p,
          parentIds: p.parentIds?.some((x) => toRemove.has(x))
            ? p.parentIds.filter((x) => !toRemove.has(x))
            : p.parentIds,
          spouseId: toRemove.has(p.spouseId) ? null : p.spouseId,
          unions: p.unions ? p.unions.filter((u) => !toRemove.has(u.spouseId)) : p.unions,
        }));
      setPeopleRef(remaining);

      const newRoot = toRemove.has(rootPersonId) ? (remaining[0]?.id ?? null) : rootPersonId;
      const layout = newRoot ? buildTreeLayout(newRoot) : { nodes: [], edges: [], families: [], positions: {} };
      set({
        people: remaining,
        ...layout,
        rootPersonId: newRoot,
        selectedPersonId: toRemove.has(selectedPersonId) ? null : selectedPersonId,
        panelOpen: toRemove.has(selectedPersonId) ? false : panelOpen,
      });
    }

    if (currentTreeId) {
      await recordAuditEntry({ treeId: currentTreeId, action: "remove", personId: id, personName });
    } else {
      const entry = { id: `log${Date.now()}`, action: "remove", personName, personId: id,
        by: currentUser?.name ?? "Vous", at: new Date().toISOString().split("T")[0] };
      set((s) => ({ auditLog: [entry, ...s.auditLog] }));
    }
  },

  /**
   * Remove ONLY this person — children, spouse, and other relatives are kept.
   * Family links / unions cascade on delete in the DB; in demo mode we clean
   * up parentIds and spouse refs manually.
   */
  removePersonOnly: async (id) => {
    const { currentTreeId, currentUser, rootPersonId, selectedPersonId, panelOpen } = get();
    const removed = getPeopleRef().find((p) => p.id === id);
    const personName = removed ? getFullName(removed) : id;

    if (currentTreeId) {
      const { error } = await apiDeletePerson(id);
      if (error) return;
      await get().loadTree(currentTreeId);
    } else {
      const arr = getPeopleRef();

      const remaining = arr
        .filter((p) => p.id !== id)
        .map((p) => ({
          ...p,
          parentIds: p.parentIds?.includes(id)
            ? p.parentIds.filter((pid) => pid !== id)
            : p.parentIds,
          parentLinkTypes: p.parentLinkTypes?.[id]
            ? Object.fromEntries(Object.entries(p.parentLinkTypes).filter(([k]) => k !== id))
            : p.parentLinkTypes,
          spouseId: p.spouseId === id ? null : p.spouseId,
          unions: p.unions ? p.unions.filter((u) => u.spouseId !== id) : p.unions,
        }));
      setPeopleRef(remaining);

      const newRoot = id === rootPersonId ? (remaining[0]?.id ?? null) : rootPersonId;
      const layout = newRoot ? buildTreeLayout(newRoot) : { nodes: [], edges: [], families: [], positions: {} };
      set({
        people: remaining,
        ...layout,
        rootPersonId: newRoot,
        selectedPersonId: id === selectedPersonId ? null : selectedPersonId,
        panelOpen: id === selectedPersonId ? false : panelOpen,
      });
    }

    if (currentTreeId) {
      await recordAuditEntry({ treeId: currentTreeId, action: "remove", personId: id, personName });
    } else {
      const entry = { id: `log${Date.now()}`, action: "remove", personName, personId: id,
        by: currentUser?.name ?? "Vous", at: new Date().toISOString().split("T")[0] };
      set((s) => ({ auditLog: [entry, ...s.auditLog] }));
    }
  },
}));
